/**
 * The card's two one-step confirms (docs/market-making-ux.md, Revision 2):
 *
 *   GetOutConfirm   Exposed → [Get out for −$0.96]: a MARKET buy of the short side's missing
 *                   contracts at the best live allowed venue, under the same opportunity_id,
 *                   optionally cancelling the long side's resting bids first (on by default).
 *   MoveBidConfirm  Working, market moved away → [Move bid]: cancel that side's resting bid(s),
 *                   then rest a new limit at the re-planned default, same opportunity_id.
 *
 * Each is one order-sheet sheet (useMmSheet.ts; order-sheet 0.1.2 `opportunityId` + `modelId`:
 * the opportunity's model, none on a quote from before models), sized in
 * contracts (`{ shares }`). The numbers shown are the sheet's: venue, fill price, contracts, cost
 * and fee (exact once its preflight answers); the locked result is computed from them. Confirm
 * re-reads the sheet first: if the numbers moved (or the best venue / the re-planned bid did),
 * nothing is sent and the confirm shows the new numbers and asks again. The red "Real money"
 * frame is the confirm step; in Real the sheet's own `confirm_required` → `confirm()` follows on
 * the same press, after the re-check and the cancels. Real money follows the ticket's arming
 * (global Real + trade:execute).
 */
import { useRef, useState, type ReactNode } from "react";
import type { MoneyMode, OrderSheetClient, PositionRef, SheetInit } from "@openmarketsai/order-sheet";
import {
  legRefsFrom,
  placedLegs,
  sheetPreview,
  sheetShares,
  type GetOut,
  type MmEntry,
  type MoveBid,
  type OppBook,
  type PlacedLeg,
  type SheetFigures,
} from "../../lib/marketMaking.js";
import { issueText } from "../../lib/ticketModel.js";
import { fmtPrice } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { btnBase, btnIdle, btnPrimary, fmtBid, fmtContracts, modeOf, money, signedMoney, type MarketMakingSource, type OrderAccess } from "./shared.js";
import { useMmSheet, type SubmitOutcome } from "./useMmSheet.js";

/* ===================================================================== guard */

/** Can this card place in its own money right now, and what to say when it can't. */
function useGuard(source: MarketMakingSource, entry: MmEntry) {
  const orders = source.useOrders();
  const { mode, wantsReal, setMode } = source.useMode();
  const mismatch = (entry.ccy === "USD") !== (mode === "real");
  const realBlocked = wantsReal && !orders.canTradeReal;
  const ok = !mismatch && !realBlocked && orders.client === "ok" && !!orders.om;
  const blocker: ReactNode = mismatch ? (
    <div className="flex flex-wrap items-center gap-2 text-[12px] text-amber">
      <span className="min-w-0 flex-1">
        This opportunity is {entry.ccy === "ATLAS" ? "Paper" : "Real"}; your trading mode is {mode === "real" ? "Real" : "Paper"}. New orders have to be in the same money.
      </span>
      <button type="button" onClick={() => setMode(entry.ccy === "ATLAS" ? "paper" : "real")} className={`${btnBase} ${btnPrimary}`}>
        Switch to {entry.ccy === "ATLAS" ? "Paper" : "Real"}
      </button>
    </div>
  ) : orders.client !== "ok" || !orders.om ? (
    <p className="text-[12px] text-amber">
      {orders.client === "signed_out" ? "Sign in with OpenMarkets to place orders." : "Placing orders needs a Sign in with OpenMarkets session (a pasted API key can't place orders)."}
    </p>
  ) : realBlocked ? (
    <div className="flex flex-wrap items-center gap-2 text-[12px] text-amber">
      <span className="min-w-0 flex-1">Real orders need order placement access, which this session wasn't granted.</span>
      <button type="button" onClick={orders.unlockReal} className={`${btnBase} ${btnPrimary}`}>
        Unlock Real
      </button>
    </div>
  ) : null;
  return { orders, ok, blocker };
}

/** The confirm's frame: red "Real money" in Real, quiet in Paper. */
function Frame({ mode, title, children }: { mode: MoneyMode; title: string; children: ReactNode }) {
  const real = mode === "real";
  return (
    <div
      role="alertdialog"
      aria-label={title}
      className={`gb-fade mt-2 flex flex-col gap-2 rounded-lg border p-3 ${real ? "border-down/40 bg-down/5" : "border-border-2 bg-bg"}`}
    >
      <div className={`text-[11px] font-semibold uppercase tracking-wider ${real ? "text-down" : "text-dim"}`}>
        {title}
        {real ? " · Real money" : " · Paper"}
      </div>
      {children}
    </div>
  );
}

function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px] sm:grid-cols-3">
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[10px] uppercase tracking-wide text-faint">{k}</dt>
          <dd className="nums mt-0.5 break-words text-ink">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const confirmBtn = (mode: MoneyMode) =>
  mode === "real" ? "border-down/60 bg-down/15 text-down enabled:hover:bg-down/25" : "border-amber/60 bg-amber/15 text-amber enabled:hover:bg-amber/25";

/** The sheet's position ref, as the trade ticket builds it: the venue's liquidity hash, else position + venue. */
export function sheetRef(liquidityHash: string | null, positionHash: string, partnerId: string): PositionRef {
  return liquidityHash ? { liquidityHash } : { positionHash, partnerId };
}

/** "$2.78 + $0.07 fee" (≈ while the fee is the sheet's estimate). */
export function costText(f: SheetFigures, ccy: MmEntry["ccy"]): string {
  return `${money(f.cost, ccy)}${f.fee > 0 ? ` + ${f.exact ? "" : "≈"}${money(f.fee, ccy)} fee` : ""}`;
}

/**
 * Cancel-first, once: the ids already cancelled by an earlier press (whose order then didn't
 * go out) aren't sent again.
 */
function useCancelFirst(orders: OrderAccess) {
  const done = useRef(new Set<string>());
  const run = async (ids: string[], what: string) => {
    const todo = ids.filter((id) => !done.current.has(id));
    if (todo.length === 0) return;
    const res = await orders.cancel(todo);
    orders.afterOrder();
    const failed = new Set((res.errors ?? []).map((e) => e.router_order_id));
    for (const id of todo) if (!failed.has(id)) done.current.add(id);
    if (res.errors?.length) throw new Error(`Couldn't cancel ${what} first: ${res.errors.map((e) => e.error).join(" · ")}. Nothing new was placed.`);
  };
  return { run, count: () => done.current.size };
}

/** The sentence for a submit that didn't place (null when there's nothing to say). */
function notPlaced(out: Exclude<SubmitOutcome, { status: "placed" }>, cancelled: number): { tone: "moved" | "error"; text: string } {
  const after = cancelled > 0 ? ` (${cancelled} resting bid${cancelled === 1 ? " was" : "s were"} already cancelled.)` : "";
  switch (out.status) {
    case "armed":
      return { tone: "moved", text: `Check the numbers and confirm again.${after}` };
    case "blocked":
    case "error":
      return { tone: "error", text: `${out.message}${after}` };
    case "moved":
      return { tone: "moved", text: after };
  }
}

/* ==================================================================== get out */

export function GetOutConfirm({
  source,
  entry,
  book,
  live,
  longResting,
  onPlaced,
  onClose,
}: {
  source: MarketMakingSource;
  entry: MmEntry;
  book: OppBook;
  /** The get-out on the latest prices (recomputed every tick by the card). */
  live: GetOut | null;
  /** The long side's resting router_order_ids. */
  longResting: string[];
  onPlaced: (legs: PlacedLeg[]) => void;
  onClose: () => void;
}) {
  const { orders, ok, blocker } = useGuard(source, entry);
  // The venue and size the sheet opens on (the card's best get-out when the confirm opened).
  const [opened] = useState<GetOut | null>(live);
  const mode = modeOf(entry.ccy);
  if (!opened || !orders.om) {
    return (
      <Frame mode={mode} title="Get out">
        {!opened && <p className="text-[13px] text-dim">Nothing quotes the other side right now, so there's no price to get out at.</p>}
        {blocker}
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className={`${btnBase} ${btnIdle}`}>
            Back
          </button>
        </div>
      </Frame>
    );
  }
  return <GetOutSheet om={orders.om} orders={orders} ok={ok} blocker={blocker} entry={entry} book={book} opened={opened} live={live} longResting={longResting} onPlaced={onPlaced} onClose={onClose} source={source} />;
}

function GetOutSheet({
  om,
  orders,
  ok,
  blocker,
  source,
  entry,
  book,
  opened,
  live,
  longResting,
  onPlaced,
  onClose,
}: {
  om: OrderSheetClient;
  orders: OrderAccess;
  ok: boolean;
  blocker: ReactNode;
  source: MarketMakingSource;
  entry: MmEntry;
  book: OppBook;
  opened: GetOut;
  live: GetOut | null;
  longResting: string[];
  onPlaced: (legs: PlacedLeg[]) => void;
  onClose: () => void;
}) {
  const { format } = useOddsFormat();
  const ccy = entry.ccy;
  const mode = modeOf(ccy);
  const short = entry.sides[opened.short];
  const long = entry.sides[opened.long];
  const [ref] = useState(() => sheetRef(opened.quote.liquidityHash, short.positionHash, opened.quote.partnerId));
  const shares = sheetShares(opened.contracts);
  const init: SheetInit = {
    mode,
    orderType: "market",
    partnerId: opened.quote.partnerId,
    ...(shares != null ? { stake: { shares } } : {}),
    // A market ceiling 1¢ over the quote, following the quote (the old `best + 1¢`).
    slippage: 0.01,
    opportunityId: entry.opportunityId,
    // The opportunity's own model, or none when it has none (else 400 model_mismatch).
    ...(entry.modelId ? { modelId: entry.modelId } : {}),
  };
  const s = useMmSheet(om, ref, init, orders.canTradeReal);
  const { h } = s;
  const f = s.figures;
  const locked = f ? sheetPreview(book, opened.short, f).min : null;
  const [cancelLong, setCancelLong] = useState(true);
  const [note, setNote] = useState<{ tone: "moved" | "error"; text: string } | null>(null);
  const cancelFirst = useCancelFirst(orders);
  const issues = ok && !h.loading && !h.validation.ok ? issueText(h.validation.blocking, h, format, orders.canTradeReal) : null;
  const fill = h.quote?.expectedFill;

  const confirm = async () => {
    if (!ok || s.step || !f) return;
    setNote(null);
    if (!live && h.phase !== "unknown") {
      setNote({ tone: "moved", text: "The price moved and nothing quotes it right now, or there's nothing left to get out of. Nothing was placed." });
      return;
    }
    const out = await s.submit({
      // Follow the card: the best venue now and the contracts still missing. A change shows as
      // moved numbers, never as an order.
      prepare: (sheet) => {
        if (!live) return;
        const st = sheet.getState();
        if (live.quote.partnerId !== st.venue?.partnerId) sheet.select(live.quote.partnerId);
        const n = sheetShares(live.contracts);
        if (n != null && st.stake.value !== n) sheet.set({ stake: { shares: n } });
      },
      beforeSend: () => (cancelLong && longResting.length > 0 ? cancelFirst.run(longResting, `the ${long.label} bids`) : Promise.resolve()),
    });
    if (out.status === "placed") {
      const v = out.state.venue;
      const placed = placedLegs(out.result.raw, [
        {
          ref: { positionHash: short.positionHash, partnerId: v?.partnerId ?? opened.quote.partnerId, partnerName: v?.name ?? opened.quote.partnerName },
          label: `Get out · ${short.label}`,
          cents: out.figures ? Math.round(out.figures.maxPrice * 100) : null,
          amount: out.figures?.cost ?? 0,
        },
      ]);
      source.addLegs(entry.opportunityId, legRefsFrom(placed));
      orders.afterOrder();
      onPlaced(placed);
      return;
    }
    if (out.status === "error" && out.unknown) orders.afterOrder();
    const n = notPlaced(out, cancelFirst.count());
    if (out.status === "moved") {
      const to = out.to;
      const venue = h.sheet.getState().venue?.name ?? "the venue";
      n.text = to
        ? `${
            out.from && out.from.partnerId !== to.partnerId
              ? `The best price is now on ${venue} (${fmtPrice(to.price, null, format)})`
              : `The price moved${out.from ? ` (${fmtPrice(out.from.price, null, format)} → ${fmtPrice(to.price, null, format)} at ${venue})` : ""}`
          }. Getting out now locks in ${signedMoney(sheetPreview(book, opened.short, to).min, ccy)}. Confirm again to place it.${n.text}`
        : `The price moved and nothing quotes it right now. Nothing was placed.${n.text}`;
    }
    setNote(n);
  };

  const busy = s.step != null;
  return (
    <Frame mode={mode} title="Get out">
      {!f ? (
        <p className="text-[13px] text-dim">{h.loading || s.checking ? "Checking the price…" : (issues ?? "There's no price to get out at right now.")}</p>
      ) : (
        <>
          <p className="text-[13px] text-ink">
            Buy <b className="nums">{fmtContracts(f.contracts)}</b> {short.label} now to match your {long.label}. Locks in{" "}
            <b className={`nums ${locked! < -0.005 ? "text-down" : "text-up"}`}>{signedMoney(locked!, ccy)}</b> whoever wins.
          </p>
          <Facts
            items={[
              ["Side", short.label],
              ["Venue", h.venue?.name ?? opened.quote.partnerName],
              ["Price", `${fmtPrice(f.price, fill ? null : (h.venue?.americanOdds ?? null), format)} · market, max ${fmtPrice(f.maxPrice, null, format)}`],
              ["Contracts", fmtContracts(f.contracts)],
              ["Cost", costText(f, ccy)],
              ["Locked result", <span className={locked! < -0.005 ? "text-down" : "text-up"}>{signedMoney(locked!, ccy)}</span>],
            ]}
          />
          {fill && fill.unfilled > 0.005 && (
            <p className="text-[12px] text-amber">Only {money(fill.allocated, ccy)} of it can fill at this price right now; the rest won't buy anything.</p>
          )}
          {longResting.length > 0 && (
            <label className="flex min-h-11 items-center gap-2 text-[13px] text-ink lg:min-h-8">
              <input type="checkbox" checked={cancelLong} onChange={(e) => setCancelLong(e.target.checked)} className="h-4 w-4 accent-amber" />
              First cancel the {longResting.length} resting {long.label} bid{longResting.length === 1 ? "" : "s"} ({longResting.length === 1 ? "it'd" : "they'd"} keep adding exposure)
            </label>
          )}
          <p className="text-[11px] leading-snug text-faint">
            A market order fills at the best price up to the max; if it fills higher you get slightly fewer contracts.{" "}
            {f.exact ? "Fill and fees as OpenMarkets checked them." : s.checking ? "Checking with OpenMarkets…" : "Fees are estimates (≈)."}
          </p>
          {issues && <p className="text-[12px] text-amber">{issues}</p>}
        </>
      )}
      {note && <p className={`text-[12px] ${note.tone === "moved" ? "font-medium text-amber" : "text-down"}`}>{note.text}</p>}
      {blocker}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className={`${btnBase} ${btnIdle}`}>
          Back
        </button>
        <button
          type="button"
          disabled={!f || !ok || busy || s.checking || (!h.validation.ok && h.phase !== "unknown")}
          onClick={() => void confirm()}
          className={`${btnBase} ${confirmBtn(mode)} font-semibold`}
        >
          {s.step === "checking"
            ? "Checking…"
            : s.step === "placing"
              ? "Placing…"
              : h.phase === "unknown"
                ? "Check order status"
                : f && locked != null
                  ? `${mode === "real" ? "Confirm real get-out" : "Get out"} · ${signedMoney(locked, ccy)}`
                  : "Get out"}
        </button>
      </div>
    </Frame>
  );
}

/* =================================================================== move bid */

const moveMoved = (a: MoveBid | null, b: MoveBid | null): boolean =>
  !a || !b ? a !== b : a.newCents !== b.newCents || a.quote.partnerId !== b.quote.partnerId || a.contracts !== b.contracts;

export function MoveBidConfirm({
  source,
  entry,
  live,
  onPlaced,
  onClose,
}: {
  source: MarketMakingSource;
  entry: MmEntry;
  /** This side's move on the latest prices (null once the market came back). */
  live: MoveBid | null;
  onPlaced: (legs: PlacedLeg[]) => void;
  onClose: () => void;
}) {
  const { orders, ok, blocker } = useGuard(source, entry);
  const [opened] = useState<MoveBid | null>(live);
  const mode = modeOf(entry.ccy);
  if (!opened || !orders.om) {
    return (
      <Frame mode={mode} title="Move bid">
        {!opened && <p className="text-[13px] text-dim">The market came back to your bid, so there's nothing to move.</p>}
        {blocker}
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className={`${btnBase} ${btnIdle}`}>
            Back
          </button>
        </div>
      </Frame>
    );
  }
  return <MoveBidSheet om={orders.om} orders={orders} ok={ok} blocker={blocker} source={source} entry={entry} opened={opened} live={live} onPlaced={onPlaced} onClose={onClose} />;
}

function MoveBidSheet({
  om,
  orders,
  ok,
  blocker,
  source,
  entry,
  opened,
  live,
  onPlaced,
  onClose,
}: {
  om: OrderSheetClient;
  orders: OrderAccess;
  ok: boolean;
  blocker: ReactNode;
  source: MarketMakingSource;
  entry: MmEntry;
  opened: MoveBid;
  live: MoveBid | null;
  onPlaced: (legs: PlacedLeg[]) => void;
  onClose: () => void;
}) {
  const { format } = useOddsFormat();
  const ccy = entry.ccy;
  const mode = modeOf(ccy);
  const side = entry.sides[opened.col];
  // The re-planned bid on screen (greenbook's plan); the sheet holds its order.
  const [shown, setShown] = useState<MoveBid>(opened);
  const [gone, setGone] = useState(false);
  const [ref] = useState(() => sheetRef(opened.quote.liquidityHash, side.positionHash, opened.quote.partnerId));
  const init: SheetInit = {
    mode,
    orderType: "limit",
    partnerId: opened.quote.partnerId,
    stake: { shares: opened.contracts },
    maxPrice: opened.newCents / 100,
    opportunityId: entry.opportunityId,
    // The opportunity's own model, or none when it has none (else 400 model_mismatch).
    ...(entry.modelId ? { modelId: entry.modelId } : {}),
  };
  const s = useMmSheet(om, ref, init, orders.canTradeReal);
  const { h } = s;
  const f = s.figures;
  const [note, setNote] = useState<{ tone: "moved" | "error"; text: string } | null>(null);
  const cancelFirst = useCancelFirst(orders);
  const issues = ok && !h.loading && !h.validation.ok ? issueText(h.validation.blocking, h, format, orders.canTradeReal) : null;
  const m = shown;

  const confirm = async () => {
    if (!ok || s.step || !f || gone) return;
    setNote(null);
    if (!live && h.phase !== "unknown") {
      setGone(true);
      return;
    }
    const replanned = live && moveMoved(m, live) ? live : null;
    const out = await s.submit({
      // The market moved again: re-aim the sheet at the new plan (shows as moved numbers).
      prepare: (sheet) => {
        if (!replanned) return;
        if (replanned.quote.partnerId !== sheet.getState().venue?.partnerId) sheet.select(replanned.quote.partnerId);
        sheet.set({ stake: { shares: replanned.contracts }, maxPrice: replanned.newCents / 100 });
      },
      beforeSend: () => cancelFirst.run(m.cancelIds, `the old ${side.label} bid`),
    });
    if (replanned) setShown(replanned);
    if (out.status === "placed") {
      const v = out.state.venue;
      const placed = placedLegs(out.result.raw, [
        {
          ref: { positionHash: side.positionHash, partnerId: v?.partnerId ?? m.quote.partnerId, partnerName: v?.name ?? m.quote.partnerName },
          label: `Moved bid · ${side.label}`,
          cents: out.figures ? Math.round(out.figures.maxPrice * 100) : m.newCents,
          amount: out.figures?.cost ?? 0,
        },
      ]);
      source.addLegs(entry.opportunityId, legRefsFrom(placed));
      orders.afterOrder();
      onPlaced(placed);
      return;
    }
    if (out.status === "error" && out.unknown) orders.afterOrder();
    const n = notPlaced(out, cancelFirst.count());
    if (out.status === "moved") {
      const now = replanned ?? m;
      n.text = out.to
        ? replanned
          ? `The market moved again: the new bid is ${fmtBid(now.newCents, format)}. Confirm again to place it.${n.text}`
          : `The numbers changed (${costText(out.to, ccy)} now). Confirm again to place it.${n.text}`
        : `Nothing quotes ${side.label} on ${now.quote.partnerName} right now. Nothing was placed.${n.text}`;
    }
    setNote(n);
  };

  const busy = s.step != null;
  if (gone) {
    return (
      <Frame mode={mode} title="Move bid">
        <p className="text-[13px] text-dim">The market came back to your bid, so there's nothing to move.</p>
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className={`${btnBase} ${btnIdle}`}>
            Back
          </button>
        </div>
      </Frame>
    );
  }
  return (
    <Frame mode={mode} title="Move bid">
      <p className="text-[13px] text-ink">
        Cancel your {side.label} bid at <b className="nums">{fmtBid(m.oldCents, format)}</b> and bid <b className="nums text-amber">{fmtBid(m.newCents, format)}</b> on{" "}
        {h.venue?.name ?? m.quote.partnerName} instead (market now {fmtPrice(h.venue?.price ?? m.quote.price, h.venue ? h.venue.americanOdds : m.quote.american, format)}).
      </p>
      <Facts
        items={[
          ["Contracts", f ? fmtContracts(f.contracts) : fmtContracts(m.contracts)],
          ["Stake", f ? costText(f, ccy) : s.checking ? "checking…" : "—"],
          [
            "If both fill",
            m.marginAfter == null ? "—" : <span className={m.marginAfter <= 0 ? "text-down" : "text-ink"}>{`${Math.round(m.marginAfter * 1000) / 10}% margin`}</span>,
          ],
        ]}
      />
      {f && !f.wouldRest && <p className="text-[12px] text-amber">The market is at or under this bid now, so it would fill straight away rather than rest.</p>}
      {m.marginAfter != null && m.marginAfter <= 0 && (
        <p className="text-[12px] text-down">With your other bid where it is, both filling would lose money. Re-quote both sides under Details instead.</p>
      )}
      {issues && <p className="text-[12px] text-amber">{issues}</p>}
      {note && <p className={`text-[12px] ${note.tone === "moved" ? "font-medium text-amber" : "text-down"}`}>{note.text}</p>}
      {blocker}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className={`${btnBase} ${btnIdle}`}>
          Back
        </button>
        <button
          type="button"
          disabled={!f || !ok || busy || s.checking || (!h.validation.ok && h.phase !== "unknown")}
          onClick={() => void confirm()}
          className={`${btnBase} ${confirmBtn(mode)} font-semibold`}
        >
          {s.step === "checking"
            ? "Checking…"
            : s.step === "placing"
              ? "Moving…"
              : h.phase === "unknown"
                ? "Check order status"
                : `${mode === "real" ? "Confirm real move" : "Move bid"} to ${fmtBid(m.newCents, format)}`}
        </button>
      </div>
    </Frame>
  );
}
